import { useState } from "react";

import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  useTheme
} from "@mui/material";
import FactoryIcon from "@mui/icons-material/Factory";

import { hexToRgba } from "@aclymatepackages/converters";
import { truncateText } from "@aclymatepackages/other-helpers";

import SlideLayout from "../../../../../layouts/SlideLayout";

import OpenAiVendorCategorization from "../../../../../inputs/vendors/OpenAiVendorCategorization";

import { formatCategoryLabel } from "./helpers";

const REASON_SUBTITLES = {
  "merge-conflict": "classification conflict after a vendor merge",
  "government-entity": "flagged as a tax or regulatory payment",
  "low-confidence": "low-confidence industry"
};

const NaicsReviewSlider = ({ vendor, onClose, onUpdate, onDismiss }) => {
  const { palette } = useTheme();
  const {
    issueId,
    name,
    naicsTitle,
    emissionCategory,
    confidenceScore,
    transactionCount,
    reason,
    referenceNaicsTitle,
    referenceEmissionCategory
  } = vendor;
  const isMergeConflict = reason === "merge-conflict";
  const isGovernmentEntity = reason === "government-entity";
  // A government-entity fix has nothing to propose but its own fixed answer
  // (the detector declares no overrideFields at all), so it reuses the same
  // "suggested fix" panel as a merge conflict's reference, and skips the
  // free-text search entirely -- any override sent there would be rejected.
  const showReferencePanel = isMergeConflict || isGovernmentEntity;

  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const editVendor = (field) => (value) =>
    setSelectedSuggestion((current) => ({
      ...(current || {}),
      [field]: value
    }));

  const canConfirm =
    !!selectedSuggestion &&
    !!selectedSuggestion.naicsCode &&
    !!selectedSuggestion.naicsTitle;

  // Accepting the issue's own proposed fix (the reference panel, or a plain
  // dismiss-as-confirmed for a low-confidence issue) always matches what the
  // detector already proposed, so no override is needed -- the backend just
  // applies its stored payload. Only a free-text search pick disagrees with
  // that proposal and needs to say so explicitly.
  const onConfirmUpdate = () => {
    if (!canConfirm) {
      return;
    }
    onUpdate({
      issueId,
      override: {
        naicsCode: selectedSuggestion.naicsCode,
        naicsTitle: selectedSuggestion.naicsTitle,
        // The free-text search (OpenAiVendorCategorization) only ever
        // returns NAICS-code results, which are inherently spend-based --
        // carrying the buyer's OLD category forward (e.g. "flights") would
        // leave the just-selected naicsCode/rate ignored by the emissions
        // calc (only spend-based/company-vehicles actually use them),
        // silently making the whole resolution a no-op.
        tonsCo2ePerDollar: selectedSuggestion.tonsCo2ePerDollar,
        emissionCategory: "spend-based",
        scopeThreeCategory: 1
      }
    });
    onClose();
  };

  const onAcceptReference = () => {
    onUpdate({ issueId });
    onClose();
  };

  const onConfirmDismiss = () => {
    onDismiss(issueId);
    onClose();
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={onClose}
      header={
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <Box
              p={1.5}
              style={{
                backgroundColor: hexToRgba(palette.primary.main, 0.15),
                borderRadius: 999,
                display: "flex"
              }}
            >
              <FactoryIcon color="primary" />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h5">{name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {typeof transactionCount === "number"
                ? `${transactionCount} transaction${
                    transactionCount === 1 ? "" : "s"
                  } · ${REASON_SUBTITLES[reason] || REASON_SUBTITLES["low-confidence"]}`
                : REASON_SUBTITLES[reason] || REASON_SUBTITLES["low-confidence"]}
            </Typography>
          </Grid>
        </Grid>
      }
      content={
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Paper
              variant="outlined"
              style={{
                borderColor: palette.warning.main,
                backgroundColor: hexToRgba(palette.warning.main, 0.08)
              }}
            >
              <Box p={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {showReferencePanel ? "Your confirmed industry" : "Current industry"}
                </Typography>
                <Typography variant="body1">
                  {naicsTitle || formatCategoryLabel(emissionCategory)}
                </Typography>
                {!showReferencePanel && (
                  <Typography variant="caption" color="textSecondary">
                    {`Confidence ${Math.round(confidenceScore * 100)}%`}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
          {showReferencePanel && (
            <Grid item>
              <Paper
                variant="outlined"
                style={{
                  borderColor: palette.info.main,
                  backgroundColor: hexToRgba(palette.info.main, 0.08)
                }}
              >
                <Box p={2}>
                  <Grid container alignItems="center" justifyContent="space-between" spacing={2} wrap="nowrap">
                    <Grid item xs>
                      <Typography variant="subtitle2" gutterBottom>
                        {isMergeConflict ? "Suggested by the merged vendor" : "Suggested fix"}
                      </Typography>
                      <Typography variant="body1">
                        {isMergeConflict
                          ? referenceNaicsTitle || formatCategoryLabel(referenceEmissionCategory)
                          : "Treat as a tax or regulatory payment — not a purchase, zero reported emissions"}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button variant="outlined" color="primary" size="small" onClick={onAcceptReference}>
                        {isMergeConflict ? "Use this classification" : "Confirm — not a real purchase"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          )}
          {!isGovernmentEntity && (
            <Grid item container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="subtitle2">
                  {isMergeConflict ? "Or search for a different industry" : "Find a better match"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Describe what this vendor actually does. Pick a suggestion to
                  use it as the new industry for this vendor.
                </Typography>
              </Grid>
              <OpenAiVendorCategorization
                naicsCode={selectedSuggestion?.naicsCode || null}
                naicsTitle={selectedSuggestion?.naicsTitle || null}
                editVendor={editVendor}
                questionText="Describe what this vendor actually does"
              />
            </Grid>
          )}
        </Grid>
      }
      footer={
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            <Button onClick={onConfirmDismiss} color="inherit">
              {showReferencePanel ? "Keep my classification" : "Looks fine — dismiss"}
            </Button>
          </Grid>
          {!isGovernmentEntity && (
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={!canConfirm}
                onClick={onConfirmUpdate}
              >
                {canConfirm
                  ? `Use "${truncateText(selectedSuggestion.naicsTitle, 40)}"`
                  : "Pick an industry first"}
              </Button>
            </Grid>
          )}
        </Grid>
      }
    />
  );
};

export default NaicsReviewSlider;
